import React from 'react';
import { GoogleLogin, GoogleLoginResponse } from 'react-google-login';

import { ErrorPrompt } from '@opendoor/bricks/complex';
import { Box, Button, Flex, Image, Text } from '@opendoor/bricks/core';
import { useTheme } from '@opendoor/bricks/system/theme';
import { useObservability } from '@opendoor/observability';

type GoogleAuthErrorResp = {
  error: string;
  details: string;
};

export interface IProps {
  showNovoStyle?: boolean;
  clientId: string;
  buttonText?: string;
  onAuthed: (response: GoogleLoginResponse) => void;
}

const ButtonEero: React.FC<React.PropsWithChildren<{ onClick: React.MouseEventHandler }>> = ({
  onClick,
  children,
}) => {
  return (
    <Button
      aria-label="Sign in with Google"
      analyticsName="google-auth"
      onClick={onClick}
      backgroundColor="neutrals0"
      border="2px solid var(--colors-neutrals100)"
      color="neutrals100"
      width="100%"
      fontWeight="bold"
      display="flex"
      alignItems="center"
      justifyContent="center"
      _hover={{
        backgroundColor: 'neutrals30',
      }}
    >
      <Flex justifyContent="center" alignItems="center">
        <Image
          height="16px"
          pr={3}
          src="https://imgdrop.imgix.net/15c9f888b5c14a489e537a039f2f8883.png"
          alt="Google logo"
        />
        <Text as="span" fontSize="s1" color="neutrals100" fontWeight="bold" lineHeight="100">
          {children}
        </Text>
      </Flex>
    </Button>
  );
};

const ButtonNovo: React.FC<React.PropsWithChildren<{ onClick: React.MouseEventHandler }>> = ({
  onClick,
  children,
}) => {
  return (
    <Button
      aria-label="Sign in with Google"
      variant="secondary"
      analyticsName="google-auth"
      onClick={onClick}
      size="lg"
      minHeight={['65px', null, '74px', '82px']}
      fontSize="22px"
      width="100%"
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <Flex justifyContent="center" alignItems="center">
        <Image
          height="16px"
          pr={3}
          src="https://imgdrop.imgix.net/15c9f888b5c14a489e537a039f2f8883.png"
          alt="Google logo"
        />
        {children}
      </Flex>
    </Button>
  );
};

const ThemedButton: React.FC<React.PropsWithChildren<{ onClick: React.MouseEventHandler }>> = (
  props,
) => {
  const { name } = useTheme();
  if (name == 'novo') {
    return <ButtonNovo {...props} />;
  }

  return <ButtonEero {...props} />;
};

export const GoogleAuth: React.FC<IProps> = ({
  buttonText = 'Continue with Google',
  clientId,
  onAuthed,
}) => {
  const [errorResponse, setErrorResponse] = React.useState<GoogleAuthErrorResp | null>(null);
  const [errorMessage, setErrorMessage] = React.useState('');
  const { trackError, trackEvent } = useObservability();

  // Called, e.g., when user is in incognito mode or has otherwise blocked third-party cookies
  const onScriptLoadFailure = (response) => {
    trackError(
      'google-auth',
      new Error('script load failure'),
      {
        details: response.details,
        error_code: response.error,
      },
      { skipSentry: true },
    );
  };

  const googleResponseFailure = (response) => {
    const errorMessage = 'Sorry, the attempt to sign in with Google failed. Please try again.';

    setErrorMessage(errorMessage);
    setErrorResponse(response);

    trackError('google-auth', new Error(errorMessage), {
      details: response.details,
      error_code: response.error,
    });
  };

  const googleResponseSuccess = (response) => {
    setErrorMessage('');
    setErrorResponse(null);

    trackEvent(
      'google-auth',
      'authed',
      {
        email: response.profileObj.email,
        name: response.profileObj.name,
      },
      { categoryOverride: 'login' },
    );

    return onAuthed(response);
  };

  return (
    <Box width="100%">
      <Flex justifyContent="center" alignItems="center">
        <GoogleLogin
          clientId={clientId}
          onSuccess={googleResponseSuccess}
          onFailure={googleResponseFailure}
          onScriptLoadFailure={onScriptLoadFailure}
          render={(renderProps) => (
            <ThemedButton onClick={renderProps.onClick}>{buttonText}</ThemedButton>
          )}
        />
      </Flex>
      {errorResponse && <ErrorPrompt paddingBottom="16px" message={errorMessage} />}
    </Box>
  );
};
